import mdAxios from 'core/common/config/axios-config';
import {parseId, addTenantToQuery} from 'core/common/utils/utils';
import {buildURL} from "core/common/utils/domUtils";
import Base64 from 'base64-js';
import {observable} from "mobx";
import {
  DocumentTemplate, DocumentTemplateResource, DocumentType, DocumentCountDto,
  Document, Contract} from "core/document/documentDomain";
import {IDomainObject} from "core/form/formUtils";
import {ID} from "core/metamodel/metaConstant";
import {GridDocumentsParams} from "core/document/gridDocument";
import {GridParams} from "core/grid/domain/grid";


class  DocumentService  {


  @observable public documentTypeObject: DocumentType;
  @observable public documentTemplateObject: DocumentTemplate;
  @observable public documentCountObject: DocumentCountDto;
  @observable public contractObject: Contract;



  documentUrl(documentUuid: string, spec: any): string {
    return buildURL('/api/core/document/' + documentUuid, addTenantToQuery(spec));
  }

  documentTemporaryUrl(uuid: string, spec: any): string {
    return buildURL('/api/core/document-temporary/' + uuid, addTenantToQuery(spec));
  }


  readDocuments(params: GridDocumentsParams): Promise<Document[]> {
    return mdAxios.get('/core/document', {params})
      .then(response => response.data.map(d => new Document(d)));
  }

  saveDocument(document: Document, params: GridDocumentsParams): Promise<void> {
    return mdAxios.post('/core/document', document, {params})
      .then(response => response.data);
  }

  deleteDocument(documentUuid: string, params: GridDocumentsParams): Promise<void> {
    return mdAxios.delete('/core/document/' + documentUuid, {params})
      .then(response => response.data);
  }


  readDocumentType(documentTypeId: string): Promise<DocumentType> {
    return mdAxios.get('/document-type/' + documentTypeId)
      .then(response => new DocumentType(response.data));
  }


  saveDocumentType(documentType: DocumentType): Promise<IDomainObject> {
    return mdAxios.post('/document-type', documentType, {showPrettyError: true})
      .then((response) => response.data);
  }

  deleteDocumentType(documentTypeId: string): Promise<any> {
    return mdAxios.delete('/document-type/' + documentTypeId, {showPrettyError: true});
  }


  readDocumentTemplate(id): Promise<DocumentTemplate> {
    let strings = parseId(id);
    return mdAxios.get('/document-template/' + strings[0] + '/' + strings[1])
      .then(response => new DocumentTemplate(response.data));
  }

  saveDocumentTemplate(documentTemplate: DocumentTemplate): Promise<IDomainObject> {
    return mdAxios.post('/document-template', documentTemplate, {showPrettyError: true})
      .then((response) => response.data);
  }

  deleteDocumentTemplate(documentTypeId, templateIndex) {
    return mdAxios.delete('/document-template/' + documentTypeId + '/' + templateIndex, {showPrettyError: true});
  }

  test(documentTemplateObject): Promise<string> {
    return mdAxios.post('/core/document/test', documentTemplateObject)
      .then((response) => response.data);
  }

  documentCount(entityName: string, documentLocation: string, gridParams: GridParams): Promise<DocumentCountDto> {
    return mdAxios.post('/core/document-count/' + entityName + '/' + documentLocation, gridParams, {showPrettyError: true})
      .then(response => response.data);
  }

  loadFile(file: File): Promise<DocumentTemplateResource> {
    return new Promise((resolve, reject) => {
      let reader = new FileReader();
      reader.onload = resolve;
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    }).then( (event: any) => {
      let byteArray = new Uint8Array(event.target.result);
      return new DocumentTemplateResource({
        $new: true,
        lastModified: new Date(file.lastModified),
        name: file.name,
        size: file.size,
        contentType: file.type,
        content: Base64.fromByteArray(byteArray)
      });
    });
  };

  isUploadType(document) {
    return !document.documentType;
  }

  printDocument(documentUuid: string): void {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = this.documentUrl(documentUuid, {inline: true});
    document.body.appendChild(iframe);
    iframe.onload = () => {
      setTimeout(() => {
        iframe.focus();
        iframe.contentWindow.print();
      });
    };
  }



  // ////////// Contract ////////////////////////////////////////////////////////////////////////////////

  readContract(id: string): Promise<Contract> {
    return mdAxios.get('/core/contract/' + id)
      .then(response => new Contract(response.data));
  }

  saveContract(contract: Contract): Promise<IDomainObject> {
    return mdAxios.post('/core/contract', contract, {showPrettyError: true})
      .then((response) => response.data);
  }

  deleteContract(contractId: number): Promise<any> {
    return mdAxios.delete('/core/contract/' + contractId, {showPrettyError: true})
      .then((response) => response.data);
  }

  readPartyContracts(partyId: number): Promise<Contract[]> {
    return mdAxios.get('/core/contract/party/' + partyId)
      .then(response => response.data.map(d => new Contract(d)));
  }

}

export default new DocumentService();




// WEBPACK FOOTER //
// ./src/core/document/documentService.ts